import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "hero-sheet" }
const _hoisted_2 = { class: "hero-lead km-display" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "hero-hint"
}
const _hoisted_6 = { class: "scroll-hint" }
const _hoisted_7 = {
  id: "plaza",
  class: "plaza-block"
}
const _hoisted_8 = { class: "plaza-particle-content" }
const _hoisted_9 = {
  key: 0,
  class: "plaza-grid"
}
const _hoisted_10 = { class: "publisher" }
const _hoisted_11 = { class: "publisher-avatar" }
const _hoisted_12 = ["src"]
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "km-num" }
const _hoisted_15 = {
  key: 1,
  class: "plaza-empty"
}

import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { animate, createTimeline, scrambleText, stagger } from "animejs";
import { IconArrowRight, IconDown } from "@arco-design/web-vue/es/icon";
import { useOpinionScene } from "@/composables/useOpinionScene";
import ParticleScroll from "@/components/canvasui/ParticleScroll.vue";
import { CREATION_SCENE_DURATION, resolveSceneWheelAction } from "@/utils/opinionSceneFlow.mjs";
import { listMyMetricSpaceRecords, listPublicSpaces, PublicMetricSpaceListItem } from "@/api/spaceController";
import { useLoginUserStore } from "@/store/userStore";


export default /*@__PURE__*/_defineComponent({
  __name: 'HomePage',
  setup(__props) {

const homeEl = ref<HTMLElement>();
const router = useRouter();
const userStore = useLoginUserStore();
const { progress: sceneProgress, selection: sceneSelection } = useOpinionScene();
const departing = ref(false);
const heroEntry = ref<HTMLElement>();
const connectCount = ref(0);
const topics = ref<PublicMetricSpaceListItem[]>([]);
const myCreatedSlugs = ref(new Set());
const topicPath = (topic: PublicMetricSpaceListItem) =>
  myCreatedSlugs.value.has(topic.slug) ? `/s/${topic.slug}/results` : `/s/${topic.slug}`;
const scrollProgress = ref(0);
let keywordPulseTimer: number | undefined;
let snapTimer: number | undefined;
let creationTimer: number | undefined;
let lastScrollTop = 0;
const heroWords = [
  { t: "从一个", break: true },
  { t: "问题", hi: true },
  { t: "开始。" },
  { t: "让不同声音", break: true },
  { t: "汇入", hi: true },
  { t: "。" },
  { t: "No opinion stands alone.", break: true, en: true },
];
const enterCreation = () => {
  if (departing.value) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    router.push("/space/create");
    return;
  }
  departing.value = true;
  sceneSelection.value = true;
  creationTimer = window.setTimeout(async () => {
    const failure = await router.push("/space/create");
    if (failure) {
      departing.value = false;
      sceneSelection.value = false;
    }
  }, CREATION_SCENE_DURATION);
};
const onSceneWheel = (event: WheelEvent) => {
  const action = resolveSceneWheelAction({
    page: "home",
    deltaY: event.deltaY,
    scrollTop: homeEl.value?.scrollTop || 0,
  });
  if (action === "enter-creation") enterCreation();
};
const onHeroMove = (e: PointerEvent) => {
  if (
    !heroEntry.value ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
    return;
  const cx = e.clientX / window.innerWidth - 0.5;
  const cy = e.clientY / window.innerHeight - 0.5;
  heroEntry.value.style.transform = `translate3d(${cx * -16}px, ${
    cy * -12
  }px, 0)`;
};
const updateProgress = () => {
  const el = homeEl.value;
  if (!el) return;
  const scrollRange = Math.max(1, el.scrollHeight - el.clientHeight);
  scrollProgress.value = Math.min(1, Math.max(0, el.scrollTop / scrollRange));
  sceneProgress.value = scrollProgress.value;
};
const snapScroll = () => {
  const el = homeEl.value;
  if (!el) return;
  const t = el.scrollTop,
    h = el.clientHeight,
    dir = t - lastScrollTop;
  lastScrollTop = t;
  if (snapTimer) window.clearTimeout(snapTimer);
  if (creationTimer) window.clearTimeout(creationTimer);
  snapTimer = window.setTimeout(() => {
    const now = el.scrollTop;
    if (dir > 0 && now >= h * 0.06 && now < h * 1.02) {
      el.scrollTo({ top: h, behavior: "smooth" });
    } else if (dir < 0 && now > h * 0.06 && now < h * 1.1) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, 90);
};
const loadStats = async () => {
  try {
    const r = await listPublicSpaces();
    if (r.data.code !== 0) return;
    topics.value = r.data.data || [];
  } catch {
  }
  try {
    if (!userStore.loginUser?.id || userStore.loginUser?.userRole === "notLogin") {
      myCreatedSlugs.value = new Set();
      return;
    }
    const mine = await listMyMetricSpaceRecords();
    const created = (mine.data.data as any)?.created || [];
    myCreatedSlugs.value = new Set(
      created.map((item: any) => String(item.slug || "")).filter(Boolean)
    );
  } catch {
    myCreatedSlugs.value = new Set();
  }
};
onMounted(() => {
  sceneSelection.value = false;
  loadStats();
  updateProgress();
  homeEl.value?.addEventListener("scroll", updateProgress, { passive: true });
  homeEl.value?.addEventListener("scroll", snapScroll, { passive: true });
  window.addEventListener("pointermove", onHeroMove, { passive: true });
  homeEl.value?.addEventListener("wheel", onSceneWheel, { passive: true });
  if (location.hash === "#plaza" || location.hash === "#space") {
    requestAnimationFrame(() =>
      homeEl.value?.scrollTo({ top: homeEl.value.clientHeight, behavior: "auto" })
    );
  }
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  animate(".hero-word", {
    innerHTML: scrambleText({
      cursor: "░▒▓█",
      from: "center",
      ease: "inOutQuad",
      duration: 1050,
      revealDelay: 90,
      perturbation: 0.3,
      settleDuration: 380,
    }),
    opacity: [0, 1],
    duration: 950,
    delay: stagger(80),
    ease: "outCubic",
  });
  animate(".prompt", {
    opacity: [0, 1],
    translateY: [14, 0],
    duration: 700,
    delay: 980,
    ease: "outCubic",
  });

  keywordPulseTimer = window.setTimeout(() => {
    const keywordPulse = createTimeline({ loop: true, loopDelay: 5000 });
    keywordPulse
      .add(".hero-word.hi", {
        innerHTML: scrambleText({
          cursor: "░▒▓",
          from: "center",
          ease: "outQuad",
          duration: 560,
          perturbation: 0.26,
          settleDuration: 320,
        }),
        scale: { from: 1, to: 1.12 },
        duration: 720,
        ease: "outQuad",
      })
      .add(
        ".hero-word.hi",
        { scale: 1, duration: 620, ease: "inOutQuad" },
        "<+=320"
      );
  }, 1600);
});
onBeforeUnmount(() => {
  if (keywordPulseTimer) window.clearTimeout(keywordPulseTimer);
  if (snapTimer) window.clearTimeout(snapTimer);
  homeEl.value?.removeEventListener("scroll", updateProgress);
  homeEl.value?.removeEventListener("scroll", snapScroll);
  window.removeEventListener("pointermove", onHeroMove);
  homeEl.value?.removeEventListener("wheel", onSceneWheel);
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "homeEl",
    ref: homeEl,
    class: _normalizeClass(["home", { departing: departing.value }])
  }, [
    _createElementVNode("section", _hoisted_1, [
      _createElementVNode("div", {
        class: "hero-shade",
        style: _normalizeStyle({ opacity: (1 - scrollProgress.value) * 0.72 })
      }, null, 4),
      _createElementVNode("div", {
        ref_key: "heroEntry",
        ref: heroEntry,
        class: "hero-entry",
        style: _normalizeStyle({ opacity: 1 - scrollProgress.value * 1.15 })
      }, [
        _createElementVNode("p", _hoisted_2, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(heroWords, (word, i) => {
            return _createElementVNode("span", { key: i }, [
              (word.break)
                ? (_openBlock(), _createElementBlock("br", _hoisted_3))
                : _createCommentVNode("", true),
              _createElementVNode("span", {
                class: _normalizeClass(["hero-word", { hi: word.hi, 'hero-en': word.en }])
              }, _toDisplayString(word.t), 3)
            ])
          }), 64))
        ]),
        _cache[1] || (_cache[1] = _createElementVNode("p", { class: "hero-agent-note" }, " Agent 会与你一起澄清议题、追问立场，并把分散的回答整理成可比较的观点画像。 ", -1)),
        _cache[2] || (_cache[2] = _createElementVNode("div", {
          class: "agent-capabilities",
          "aria-label": "Agent 能力"
        }, [
          _createElementVNode("span", null, "共同定义问题"),
          _createElementVNode("span", null, "循证追问"),
          _createElementVNode("span", null, "聚合群体倾向")
        ], -1)),
        _createElementVNode("button", {
          class: "prompt",
          type: "button",
          disabled: departing.value,
          onClick: enterCreation
        }, [
          _cache[0] || (_cache[0] = _createElementVNode("span", null, "与 Agent 发起一个议题", -1)),
          _createVNode(_unref(IconArrowRight))
        ], 8, _hoisted_4),
        (connectCount.value > 0)
          ? (_openBlock(), _createElementBlock("p", _hoisted_5, " 已联结 " + _toDisplayString(connectCount.value) + " 个观点 ", 1))
          : _createCommentVNode("", true)
      ], 4),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_unref(IconDown)),
        _cache[3] || (_cache[3] = _createTextVNode("下滑看正在发生的议题", -1))
      ])
    ]),
    _createElementVNode("section", _hoisted_7, [
      _createVNode(ParticleScroll, {
        class: "plaza-particle-scroll",
        point: 0.6,
        band: 620,
        density: 2,
        size: 1.5,
        spread: 460,
        gravity: 0.15,
        drift: 0.7,
        swirl: 50,
        stagger: 0.7,
        fade: 0.6,
        settle: 1.45,
        smoothing: 1.3
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_8, [
            _cache[7] || (_cache[7] = _createElementVNode("header", { class: "plaza-head" }, [
              _createElementVNode("h1", null, "观点空间"),
              _createElementVNode("p", null, "正在发生的公开议题，点开即可参与。")
            ], -1)),
            (topics.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(topics.value, (topic) => {
                    return (_openBlock(), _createBlock(_component_router_link, {
                      key: topic.id,
                      to: topicPath(topic),
                      class: "plaza-card"
                    }, {
                      default: _withCtx(() => [
                        _cache[4] || (_cache[4] = _createElementVNode("i", {
                          class: "node",
                          "aria-hidden": "true"
                        }, null, -1)),
                        _createElementVNode("div", null, [
                          _createElementVNode("h2", null, _toDisplayString(topic.title), 1),
                          _createElementVNode("p", null, _toDisplayString(topic.description || "等待不同观点进入。"), 1),
                          _createElementVNode("span", _hoisted_10, [
                            _createElementVNode("span", _hoisted_11, [
                              (topic.publisherAvatar)
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    src: topic.publisherAvatar,
                                    alt: ""
                                  }, null, 8, _hoisted_12))
                                : (_openBlock(), _createElementBlock("b", _hoisted_13, _toDisplayString((topic.publisherName || "K").charAt(0).toUpperCase()), 1))
                            ]),
                            _createTextVNode(_toDisplayString(topic.publisherName || "KomeiC 用户"), 1)
                          ])
                        ]),
                        _createElementVNode("small", _hoisted_14, _toDisplayString(topic.confirmedCount) + " / " + _toDisplayString(topic.participantCount), 1),
                        _createVNode(_unref(IconArrowRight))
                      ]),
                      _: 2
                    }, 1032, ["to"]))
                  }), 128))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _cache[6] || (_cache[6] = _createElementVNode("p", null, "空间还很安静。", -1)),
                  _createVNode(_component_router_link, {
                    class: "km-button",
                    to: "/space/create"
                  }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode("发起第一个议题", -1)
                    ]))]),
                    _: 1
                  })
                ]))
          ])
        ]),
        _: 1
      })
    ])
  ], 2))
}
}

})